import React, { useState, useCallback } from "react";
import * as R from "ramda";
import { Query } from "react-apollo";
import { css } from "glamor";
import { Input, Icon } from "antd";

import Buttons from "@arachnys/styleguide/Button";
import { GET_TASK_PERMISSIONS } from "@arachnys/flow-app/src/page/Tasks/queries";
import { withApolloState } from "@arachnys/flow-app/src/util";
import { formatDatetimeString } from "@arachnys/flow-app/src/formatters";

const styles = {
    commentContainer: css({
        padding: 10,
        width: "100%",
        backgroundColor: "#fff",
        marginBottom: 5,
        borderRadius: 5,
    }),
    commentTitle: css({
        fontWeight: "bold",
    }),
    editIcons: css({
        textAlign: "right",
        cursor: "pointer",
    }),
    editCommentButton: css({
        margin: "5px 0 0 5px",
        minWidth: 100,
    }),
};

const CommentContainer = ({
    comment,
    edit,
    remove,
    canEditPermission,
    readOnly,
}) => {
    const [editable, setEditable] = useState(false);
    const [content, setContent] = useState(comment.content);

    const editContent = e => setContent(e.target.value);
    const cancelEditing = useCallback(() => {
        setContent(comment.content);
        setEditable(false);
    }, [comment]);
    const applyEditing = useCallback(() => {
        setEditable(false);
        edit({
            ...comment,
            content,
        });
    }, [comment, content, edit]);

    return (
        <div
            data-testid="disposition-comment-container"
            className={styles.commentContainer}
        >
            <div className={styles.commentTitle}>
                {comment.owner.firstName}
                {" on "}
                {formatDatetimeString({
                    str: comment.createdDate,
                    precision: "MINUTE",
                })}
            </div>
            <div>
                {editable ? (
                    <>
                        <Input.TextArea
                            rows={4}
                            value={content}
                            onChange={editContent}
                            data-testid="disposition-comment-edit-textarea"
                        />
                        <div className={styles.editIcons}>
                            <Buttons.Secondary
                                data-testid="disposition-comment-edit-cancel"
                                className={`${styles.editCommentButton}`}
                                onClick={cancelEditing}
                            >
                                Cancel
                            </Buttons.Secondary>
                            <Buttons.Primary
                                data-testid="disposition-comment-edit-submit"
                                className={`${styles.editCommentButton}`}
                                onClick={applyEditing}
                                disabled={!content}
                            >
                                Edit
                            </Buttons.Primary>
                        </div>
                    </>
                ) : (
                    content
                )}
            </div>
            {(comment.isUnsaved || canEditPermission) &&
                !editable &&
                !readOnly && (
                    <div className={styles.editIcons}>
                        <Icon
                            type="form"
                            data-testid="disposition-comment-edit"
                            onClick={() => setEditable(!editable)}
                        />
                        <Icon
                            type="delete"
                            data-testid="disposition-comment-delete"
                            onClick={() => remove(comment.id)}
                        />
                    </div>
                )}
        </div>
    );
};

const Comments = ({ comments, editComment, deleteComment, readOnly }) => {
    if (!comments || !comments.length) {
        return null;
    }

    return (
        <Query query={GET_TASK_PERMISSIONS} fetchPolicy="cache-only">
            {withApolloState(apolloProps => {
                const canEditAllComments = R.path(
                    [
                        "data",
                        "permissions",
                        "task",
                        "canEditDispositionComments",
                    ],
                    apolloProps
                );
                return (
                    <div className={styles.genericViewerItemContainer}>
                        <div className={styles.genericViewerTitle}>
                            Comments:
                        </div>
                        <div className={styles.genericViewerValue}>
                            {comments.map(comment => (
                                <CommentContainer
                                    canEditPermission={canEditAllComments}
                                    readOnly={readOnly}
                                    key={comment.id}
                                    edit={editComment}
                                    remove={deleteComment}
                                    comment={comment}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </Query>
    );
};

export default Comments;
